import { EDatasourceType, ESdChangeReason, IActionDefinitionDto, IDatasourceDto } from "@kuika/kuika-cl-model";
import _ from "lodash";
import ObjectHash from "object-hash";
import { FormInstance } from "rc-field-form";
import * as React from "react";
import { connect } from "react-redux";
import {
  IConnectionStringParams,
  IExtraConnectionStringParam
} from "../../../services/connection-string-builder-service";
import { DatasourceService } from "../../../services/datasource-service";
import { IWithSDProps, withSD } from "../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../shared/pure-sd";
import { KMainFunctions } from "../../../shared/utility/main-functions";
import { getProcessUsedActionIds } from "../../../shared/utility/process-action-usage";
import { SaveStatus } from "../../../shared/utility/save-status";
import { AppState } from "../../../store/root-reducer";
import { setNextCallbackFunction, setShowModal } from "../../../store/unsaved-changes-modal/actions";
import { runParkedNavigation } from "../../../store/unsaved-changes-modal/run-parked-navigation";
import CloseConfirmationModal from "../../common/close-confirmation-modal";
import { type KuikaButtonStatus } from "../../ui-kit/kuika-button/kuika-button";
import KuikaFormPage, {
  KuikaFormAsideBlock,
  KuikaFormField,
  KuikaFormPageVerifyResult,
  KuikaFormSection
} from "../../ui-kit/kuika-form-page/kuika-form-page";
import KuikaInput from "../../ui-kit/kuika-input/kuika-input";
import KuikaSvg from "../../ui-kit/kuika-svg/kuika-svg";
import KuikaTabs from "../../ui-kit/kuika-tabs/kuika-tabs";
import { ConnectionParametersGrid } from "./connection-parameters-grid";
import { ConnectionRenameTitle } from "./connection-rename-title";
import { requestDatasourceReveal } from "./datasource-reveal";
import "./create-connection-form.scss";
import "./editor-bar-surface.scss";
import { ConnectionSwitcherChip } from "./editor-switcher/connection-switcher-chip";
import {
  extraParamKeyProblem,
  ISqlConnectionEngine,
  ISqlConnectionField,
  withoutBlankExtraParams
} from "./sql-connection-engines";

/** The two ways to describe the same endpoint. Labels double as the segment's values. */
const MODE_FIELDS = "Enter fields";
const MODE_CONNECTION_STRING = "Connection string";

/**
 * A datasource whose type has no tables to read back after saving. Checked against the
 * types the API returns, not the form's own, so the list is the same for every engine.
 */
const TYPES_WITHOUT_TABLES: EDatasourceType[] = [
  EDatasourceType.RestApi,
  EDatasourceType.RestApiWOSwagger,
  EDatasourceType.CSharpFunctions,
  EDatasourceType.BleService,
  EDatasourceType.SAP
];

export interface ISqlConnectionFormState {
  /** Where the save stands. Owned by `SaveStatus`, never set directly. */
  saveStatus: KuikaButtonStatus;
  isNameOnEdit: boolean;
  datasourceName: string;
  shouldUseConnectionString: boolean;
  isTestLoading: boolean;
  /** Last Test outcome, shown as a chip in the rail. `null` until first run. */
  testResult: KuikaFormPageVerifyResult | null;
  /** What the fields currently build, password masked — recomputed on every edit. */
  resolvedConnectionString: string;
  /** Actions hanging off this datasource, and how many of those a process runs. */
  actionCount: number;
  processActionCount: number;
  isPasswordVisible: boolean;
}

export interface ISqlConnectionFormProps {
  engine: ISqlConnectionEngine;
  editingDatasource?: IDatasourceDto;
  showModal: boolean;
  nextCallback?: () => void;
  setNextCallbackFunction: (cb?: () => void) => void;
  setShowModal: (showModal: boolean) => void;
}

class SqlConnectionForm extends PureSdComponent<ISqlConnectionFormProps & IWithSDProps, ISqlConnectionFormState> {
  formRef = React.createRef<FormInstance>();

  objectHash: string = "";

  private readonly saveStatus: SaveStatus = new SaveStatus((saveStatus: KuikaButtonStatus): void => {
    this.setState({ saveStatus });
  });

  constructor(props: ISqlConnectionFormProps & IWithSDProps) {
    super(props);
    this.state = {
      saveStatus: "idle",
      isNameOnEdit: false,
      datasourceName: "",
      shouldUseConnectionString: false,
      isTestLoading: false,
      testResult: null,
      resolvedConnectionString: "",
      actionCount: 0,
      processActionCount: 0,
      isPasswordVisible: false
    };
  }

  componentWillUnmount(): void {
    this.saveStatus.dispose();
  }

  componentDidMount() {
    this.handleLoadDatasource();
    this.loadUsage();
  }

  componentDidUpdate(prevProps: ISqlConnectionFormProps & IWithSDProps) {
    if (
      this.props.editingDatasource?.id !== prevProps.editingDatasource?.id ||
      this.props.sd.activeCreatingDatasourceName !== prevProps.sd.activeCreatingDatasourceName
    ) {
      this.handleLoadDatasource();
    }
    // A save the pending navigation asked for ("Update & close") is still running or showing
    // its ✓: its own close runs the navigation. Asking again now would re-open the confirmation.
    if (this.props.nextCallback && this.saveStatus.canStart) {
      this.handleCloseDatasource();
    }
  }

  /** Every field the engine declares, endpoint then credentials. */
  private get fields(): ISqlConnectionField[] {
    return [...this.props.engine.endpointFields, ...this.props.engine.credentialFields];
  }

  private testId(suffix: string): string {
    return `${this.props.engine.testIdPrefix}${suffix}`;
  }

  // The two sources the canvas already reads for its "used in a process" banner, moved
  // beside the fields that would break it. Only meaningful once the datasource exists.
  private loadUsage = async (): Promise<void> => {
    const datasourceId: string | undefined = this.props.editingDatasource?.id;
    if (!datasourceId) return;

    const own: IActionDefinitionDto[] = (this.props.sd?.actionDefinitions ?? []).filter(
      (action) => String(action.kDatasource_Id) === String(datasourceId)
    );
    const usedInProcess: Set<string> = await getProcessUsedActionIds(
      this.props.sd?.workFlowList,
      this.props.sd?.project?.id
    );
    this.setState({
      actionCount: own.length,
      processActionCount: own.filter((action) => usedInProcess.has((action.id ?? "").toLowerCase())).length
    });
  };

  /** Whether the fields say anything yet: the builder writes every key it knows, filled or not. */
  private hasAnyValue(parsed: IConnectionStringParams): boolean {
    const inFields: boolean = this.fields.some((field: ISqlConnectionField): boolean => {
      const value = parsed[field.key];
      return typeof value === "string" && value.trim() !== "";
    });
    const inExtras: boolean = (parsed.extraParams ?? []).some(
      (param: IExtraConnectionStringParam): boolean => (param?.key ?? "").trim() !== ""
    );

    return inFields || inExtras;
  }

  // The builder writes every string field, so the password would be on screen in clear.
  // Masked for display only; the saved value is still built from the form.
  private refreshResolved = (): void => {
    const values = this.formRef.current?.getFieldsValue();
    const parsed: IConnectionStringParams | undefined = values?.parsedConnectionString;
    if (!parsed) return;

    // Empty fields still build `Host=;Port=;User=…`, which says nothing and would keep the
    // block on screen after the values that summoned it were deleted.
    if (!this.hasAnyValue(parsed)) {
      this.setState({ resolvedConnectionString: "" });
      return;
    }

    const built: string = this.props.engine.build(parsed);
    const passwordField: ISqlConnectionField | undefined = this.fields.find(
      (field: ISqlConnectionField): boolean => field.isPassword === true
    );
    // Only where there is one to hide: six dots over an empty password read as a password.
    const hasPassword: boolean =
      !!passwordField && typeof parsed[passwordField.key] === "string" && parsed[passwordField.key] !== "";
    const masked: string =
      passwordField && hasPassword
        ? built.replace(new RegExp(`(^|;)(${passwordField.key}=)[^;]*`, "i"), "$1$2••••••")
        : built;
    this.setState({ resolvedConnectionString: masked });
  };

  handleLoadDatasource = () => {
    const {
      editingDatasource,
      sd: { activeCreatingDatasourceName }
    } = this.props;

    let state = { ..._.cloneDeep(this.state) };
    const parsedConnectionString: IConnectionStringParams = { extraParams: [] };
    this.fields.forEach((field: ISqlConnectionField): void => {
      parsedConnectionString[field.key] = "";
    });

    const fieldsValue = {
      name: activeCreatingDatasourceName,
      connectionString: "",
      parsedConnectionString
    };

    state.datasourceName = activeCreatingDatasourceName ?? "";
    state.isNameOnEdit = false;
    state.shouldUseConnectionString = false;

    if (editingDatasource) {
      fieldsValue.name = editingDatasource.name;
      fieldsValue.connectionString = editingDatasource.connectionString;
      fieldsValue.parsedConnectionString = this.props.engine.parse(editingDatasource.connectionString);
      state.datasourceName = editingDatasource.name;
    }

    this.setState(state, () => {
      this.formRef.current?.setFieldsValue(fieldsValue);
      this.objectHash = this.getObjectHash(state);
      this.refreshResolved();
    });
  };

  getObjectHash(state?: ISqlConnectionFormState): string {
    const { datasourceName, shouldUseConnectionString } = state || this.state;
    const values = this.formRef.current?.getFieldsValue();
    const { connectionString, parsedConnectionString } = values ?? {};

    // The parameter grid's trailing draft row is where the next parameter goes, not an edit: it
    // arrives after the baseline is taken, so it is left out of both sides of the compare.
    return ObjectHash({
      datasourceName,
      connectionStringValue: shouldUseConnectionString
        ? connectionString
        : parsedConnectionString && withoutBlankExtraParams(parsedConnectionString)
    });
  }

  closeDataSource() {
    if (!runParkedNavigation(this.props.nextCallback, this.props.setNextCallbackFunction)) {
      this.props.sd.cancelCreatingOrEditingDatasource();
    }
  }

  handleCloseDatasource() {
    if (this.objectHash === this.getObjectHash()) {
      this.closeDataSource();
      return;
    }

    this.props.setShowModal(true);
  }

  saveDatasource = async () => {
    const { engine } = this.props;
    if (this.state.isNameOnEdit) {
      KMainFunctions.displayWarningNotification(
        `You should apply your Datasource Name before save the ${engine.copyName} Datasource`
      );
      return;
    }

    // The commit button is live again as soon as the ✓ replaces the spinner, so a second press
    // would re-fire the save behind the closing sheet.
    if (!this.saveStatus.canStart) return;
    this.saveStatus.start();
    const form = this.formRef.current!.getFieldsValue();
    const ds = {} as IDatasourceDto;

    if (this.props.editingDatasource?.id) {
      ds.id = this.props.editingDatasource.id;
    }

    const isEqualToScreenName = this.props.sd?.screenList?.find(
      (screen) => screen.name.toLowerCase() === this.state.datasourceName?.toLowerCase()
    );
    const isEqualToWfName = this.props.sd?.workFlowList?.find(
      (wf) => wf.name.toLowerCase() === this.state.datasourceName?.toLowerCase()
    );
    const isEqualToDatasourceName = this.props.sd?.datasources?.some(
      (datasource) =>
        datasource.name.toLowerCase() === this.state.datasourceName?.toLowerCase() &&
        datasource.id !== this.props.editingDatasource?.id
    );
    if (isEqualToDatasourceName) {
      this.saveStatus.reset();
      KMainFunctions.displayWarningNotification("Datasource name already exist.");
      return;
    }
    if (isEqualToScreenName || isEqualToWfName) {
      this.saveStatus.reset();
      KMainFunctions.displayWarningNotification("Datasource name can not be equal to screen name or Process name.");
      return;
    }

    try {
      ds.datasourceType = engine.datasourceType;
      ds.name = this.state.datasourceName;
      ds.connectionString = this.state.shouldUseConnectionString
        ? form.connectionString
        : engine.build(form.parsedConnectionString);

      const res = await DatasourceService.SaveDatasource(ds);
      if (res.status !== 200) {
        // A resolved response that is not 200: the `finally` reset used to rescue this branch.
        this.saveStatus.fail();
        return;
      }
      await this.props.sd.refreshDatabases();
      res.data?.forEach((datasource: IDatasourceDto): void => {
        if (!TYPES_WITHOUT_TABLES.includes(datasource.datasourceType)) {
          this.props.sd.getTables(datasource.id);
        }
      });
      // The ✓ goes up first and the sheet leaves after it — see `SaveStatus`.
      // A close confirmation this save answered ("Update & close") steps aside at once, so the ✓ on
      // the button is what is seen before the sheet leaves.
      this.props.setShowModal(false);
      this.saveStatus.succeed((): void => {
        // The list behind the sheet lights the row up; the button has already said "Saved".
        requestDatasourceReveal({ name: ds.name });
        // Through the close path, not straight to cancel: saving is how "Update & close"
        // answers a close REQUEST, and a raw cancel drops the request where it stands —
        // whatever the user was opening never opened, and the stale callback then closed
        // the next editor to mount.
        this.closeDataSource();
        this.props.sd.clearCSharpTempValues();
      });
    } catch (err) {
      this.saveStatus.fail();
      const backendMessage: string | undefined = KMainFunctions.getApiErrorMessage(err);
      KMainFunctions.displayWarningNotification(backendMessage ?? "Error while saving connection");
    }
  };

  // The outcome lands on a chip in the rail rather than a toast: it has to stay readable
  // while the fields it refers to are being corrected.
  testConnection = () => {
    this.setState({ isTestLoading: true, testResult: null });
    this.formRef.current
      ?.validateFields()
      .then(async (formValues) => {
        const connectionString: string = this.state.shouldUseConnectionString
          ? formValues.connectionString
          : this.props.engine.build(formValues.parsedConnectionString);

        const response = await this.props.engine.test(connectionString);
        if (response.status === 200) {
          this.setState({ testResult: { ok: response.data.success, message: response.data.message } });
        }
      })
      // A rejection here is the form's own validation result; the fields already say what
      // is missing, so the chip stays empty rather than repeating it.
      .catch(() => {})
      .finally(() => {
        this.setState({ isTestLoading: false });
      });
  };

  handleSubmitNameChange = () => {
    const errors = this.formRef.current?.getFieldError("name");
    if (errors && errors.length > 0) {
      return;
    }
    this.setState({ isNameOnEdit: false, datasourceName: this.formRef.current?.getFieldValue("name") });
  };

  handleAbortNameChange = () => {
    if (!this.state.datasourceName) {
      KMainFunctions.displayWarningNotification("Please enter a valid Datasource name");
      return;
    }

    this.formRef.current?.setFieldsValue({
      ...this.formRef.current.getFieldsValue(),
      name: this.state.datasourceName
    });
    this.setState({ isNameOnEdit: false });
  };

  handleModeOnChange = (shouldUseConnectionString: boolean) => {
    const formValues = this.formRef.current?.getFieldsValue();

    this.setState({ shouldUseConnectionString }, () => {
      if (shouldUseConnectionString) {
        formValues.connectionString = this.props.engine.build(formValues.parsedConnectionString);
      } else if (formValues.connectionString) {
        formValues.parsedConnectionString = this.props.engine.parse(formValues.connectionString);
      }
      this.formRef.current?.setFieldsValue(formValues);
      this.refreshResolved();
    });
  };

  private renderName = (title: string): React.ReactNode => (
    <ConnectionRenameTitle
      title={title}
      identity={
        <ConnectionSwitcherChip
          sd={this.props.sd}
          setNextCallbackFunction={this.props.setNextCallbackFunction}
          current={this.props.editingDatasource}
          name={this.state.datasourceName}
          datasourceType={this.props.engine.datasourceType}
          triggerTestId="sqlConnectionSwitcherTrigger"
          onManageAll={() => this.handleCloseDatasource()}
        />
      }
      isEditing={this.state.isNameOnEdit}
      canDiscard={Boolean(this.state.datasourceName)}
      onBeginEdit={() => this.setState({ isNameOnEdit: true })}
      onSubmit={this.handleSubmitNameChange}
      onDiscard={this.handleAbortNameChange}
      placeholder="Datasource Name"
      rules={[
        { required: true, message: "Please enter a Datasource name" },
        { pattern: /^[A-Za-z][A-Za-z0-9 ]+$/, message: "Datasource name is invalid" }
      ]}
      renameTestId={this.testId("ConnectionRenameButton")}
      inputTestId={this.testId("ConnectionNameInput")}
      submitTestId={this.testId("ConnectionNameSubmitButton")}
    />
  );

  private renderField = (field: ISqlConnectionField): React.ReactNode => {
    const rules = field.requiredMessage ? [{ required: true, message: field.requiredMessage }] : undefined;

    return (
      <KuikaFormField
        key={field.key}
        name={["parsedConnectionString", field.key]}
        label={field.label}
        extra={field.extra}
        rules={rules}
      >
        <KuikaInput
          size="md"
          data-testid={field.testId}
          // These are a server's credentials, not the designer's own login. Without this the
          // browser's password manager reads "text + password" as a sign-in form and fills the
          // Kuika account in; Chrome ignores "off" on a password box but honours "new-password".
          autoComplete={field.isPassword ? "new-password" : "off"}
          type={field.isPassword && !this.state.isPasswordVisible ? "password" : "text"}
          rightUnit={
            field.isPassword ? (
              <KuikaSvg
                name={this.state.isPasswordVisible ? "eye" : "eye-closed"}
                pointer
                onClick={() => this.setState({ isPasswordVisible: !this.state.isPasswordVisible })}
              />
            ) : undefined
          }
        />
      </KuikaFormField>
    );
  };

  private renderParameters(): React.ReactNode {
    const { engine } = this.props;
    return (
      <div className="kuika-form-section__wide">
        <ConnectionParametersGrid
          testIdPrefix={engine.testIdPrefix}
          keyProblem={(params: IExtraConnectionStringParam[] | undefined, index: number): string | null =>
            extraParamKeyProblem(engine, params, index)
          }
        />
      </div>
    );
  }

  // Both blocks are conditional rather than shown empty: a pasted connection string is
  // authored, not derived, and a datasource that does not exist yet has no dependents.
  private renderAside(): React.ReactNode {
    const showResolved: boolean = !this.state.shouldUseConnectionString && !!this.state.resolvedConnectionString;
    const showUsage: boolean = !!this.props.editingDatasource?.id && this.state.actionCount > 0;
    if (!showResolved && !showUsage) return undefined;

    return (
      <>
        {showResolved && (
          <KuikaFormAsideBlock title="Resolves to">
            <code className="kuika-connection-form__resolved">{this.state.resolvedConnectionString}</code>
          </KuikaFormAsideBlock>
        )}
        {showUsage && (
          <KuikaFormAsideBlock title="Used by">
            <span className="kuika-connection-form__usage">
              {this.state.actionCount} {this.state.actionCount === 1 ? "action" : "actions"} on this datasource
            </span>
            {this.state.processActionCount > 0 && (
              <div className="kuika-connection-form__impact">
                <KuikaSvg name="alert-triangle" width={13} height={13} />
                <span>
                  <b>
                    {this.state.processActionCount} of them {this.state.processActionCount === 1 ? "runs" : "run"} in a
                    process.
                  </b>{" "}
                  Changing this connection affects older versions of that process.
                </span>
              </div>
            )}
          </KuikaFormAsideBlock>
        )}
      </>
    );
  }

  render() {
    const { engine } = this.props;
    const { shouldUseConnectionString } = this.state;

    return (
      <>
        <KuikaFormPage
          className="kuika-connection-form"
          title={this.state.datasourceName}
          formName="dsModal"
          formRef={this.formRef}
          onFinish={this.saveDatasource}
          onValuesChange={this.refreshResolved}
          aside={this.renderAside()}
          // An UPDATE reads nothing back and opens nothing, so the note is for a new one only.
          afterCreate={this.props.editingDatasource ? undefined : engine.afterCreate}
          status={this.state.saveStatus}
          renderTitle={this.renderName}
          submitLabel={this.props.editingDatasource ? "UPDATE" : "CREATE"}
          submitTestId={this.testId("ConnectionSubmitButton")}
          cancelTestId={this.testId("ConnectionCancelButton")}
          onCancel={() => this.handleCloseDatasource()}
          verify={{
            label: "Test connection",
            onVerify: this.testConnection,
            isVerifying: this.state.isTestLoading,
            result: this.state.testResult,
            testId: this.testId("TestConnectionButton"),
            resultTestId: this.testId("TestConnectionResult")
          }}
        >
          <KuikaFormSection
            legend="Endpoint"
            hint="Where Kuika connects"
            columns={2}
            testId={this.testId("EndpointSection")}
            mode={
              <KuikaTabs
                className="kuika-connection-form__mode"
                options={[
                  // Per-option hooks, because the toggle this replaced was addressed as
                  // `.ant-switch` and a segmented control has no single clickable target.
                  { value: MODE_FIELDS, label: MODE_FIELDS, dataTestId: this.testId("ModeFieldsTab") },
                  {
                    value: MODE_CONNECTION_STRING,
                    label: MODE_CONNECTION_STRING,
                    dataTestId: this.testId("ModeConnectionStringTab")
                  }
                ]}
                value={shouldUseConnectionString ? MODE_CONNECTION_STRING : MODE_FIELDS}
                onChange={(value: string) => this.handleModeOnChange(value === MODE_CONNECTION_STRING)}
                dataTestId={this.testId("ConnectionModeTabs")}
              />
            }
          >
            {shouldUseConnectionString ? (
              <KuikaFormField
                name="connectionString"
                label="Connection string"
                className="kuika-form-section__wide"
                rules={[{ required: true, message: `Please enter ${engine.copyName} connection string` }]}
              >
                <KuikaInput size="md" data-testid={this.testId("ConnectionStringInput")} />
              </KuikaFormField>
            ) : (
              engine.endpointFields.map(this.renderField)
            )}
          </KuikaFormSection>

          {!shouldUseConnectionString && (
            <KuikaFormSection
              legend="Credentials"
              hint="Part of the connection string"
              columns={2}
              testId={this.testId("CredentialsSection")}
            >
              {engine.credentialFields.map(this.renderField)}
            </KuikaFormSection>
          )}

          {!shouldUseConnectionString && (
            <KuikaFormSection
              legend="Parameters"
              hint="Appended to the connection string"
              testId={this.testId("ParametersSection")}
            >
              {this.renderParameters()}
            </KuikaFormSection>
          )}
        </KuikaFormPage>

        <CloseConfirmationModal
          itemName={this.state.datasourceName}
          closeType={`${engine.copyName} Connection`}
          triggerModal={this.props.showModal}
          isLoading={this.state.saveStatus === "loading"}
          updateAndCloseHandler={() => this.formRef.current?.submit()}
          closeWithoutSaveHandler={() => {
            this.props.setShowModal(false);
            this.closeDataSource();
          }}
          cancel={() => {
            this.props.setNextCallbackFunction();
            this.props.setShowModal(false);
          }}
        />
      </>
    );
  }

  checkNeedRender = (_changeReason: ESdChangeReason): boolean => {
    return false;
  };
}

const mapStateToProps = (state: AppState) => ({
  nextCallback: state.unsavedChangesModal.nextCallback,
  showModal: state.unsavedChangesModal.showModal
});

const mapDispatchToProps = (dispatch) => ({
  setNextCallbackFunction: (cb?: () => void) => dispatch(setNextCallbackFunction(cb)),
  setShowModal: (showModal: boolean) => dispatch(setShowModal(showModal, null))
});

const ConnectedSqlConnectionForm = connect(
  mapStateToProps,
  mapDispatchToProps
)(withSD<ISqlConnectionFormProps>(SqlConnectionForm));

export interface ISqlConnectionFormOwnProps {
  editingDatasource?: IDatasourceDto;
}

/**
 * Binds one engine to the shared form, so each `create-connection-*` file stays the
 * mount point its callers already import and nothing else.
 */
export const createSqlConnectionForm = (
  engine: ISqlConnectionEngine
): React.FunctionComponent<ISqlConnectionFormOwnProps> => {
  const EngineForm = (props: ISqlConnectionFormOwnProps): React.ReactElement => (
    <ConnectedSqlConnectionForm engine={engine} {...props} />
  );
  EngineForm.displayName = `SqlConnectionForm(${engine.copyName})`;
  return EngineForm;
};
